import { IDemerit, IDemeritsDetails, NotFoundError } from 'fitbeat.models';
import { INotificationService } from 'fitbeat.notification-service';
import { BookingsConfig } from '../../config';
import { UserProfile } from '../../entities';
import { IUserProfileRepository } from '../../persistence';
import { IDemeritsManager } from './idemeritsmanager';

export class DemeritsManager implements IDemeritsManager {
  constructor(
    private userProfileRepository: IUserProfileRepository,
    private notificationService?: INotificationService,
  ) { }

  public async addDemerit(userId: string, demeritType: IDemerit): Promise<UserProfile> {
    const user = await this.userProfileRepository.get(userId);

    if (!user) {
      throw new NotFoundError('user', userId);
    }

    if (!user.demerits) {
      user.demerits = [];
    }
    user.demerits.push(demeritType);
    if (this.notificationService) {
      const notification: IDemeritsDetails = {
        recipientUserId: user.uid,
        recipientUserFirstname: user.firstname,
        date: demeritType.sessionTime,
        timezone: demeritType.timezone,
      };
      if (await this.shouldBePenalisedForAvailability(user, demeritType.sessionTime)) {
        this.notificationService.demeritCausingPenalisation(notification)
          .catch((e) => { /* no logger, no-op */ });
      } else {
        this.notificationService.firstDemerit(notification)
          .catch((e) => { /* no logger, no-op */ });
      }
    }
    if (!BookingsConfig.getDemeritsEnabled()) {
      user.demerits = [];
    }
    return this.userProfileRepository.update(user);
  }

  public getDemerits(user: UserProfile, dateFrom: Date, dateTo: Date): IDemerit[] {
    const fromNumber = dateFrom.getTime();
    const toNumber = dateTo.getTime();
    const demerits: IDemerit[] = user.demerits.filter((demerit: IDemerit) => {
      const dateNumber = demerit.date.getTime();
      return dateNumber <= toNumber && dateNumber >= fromNumber;
    });
    return demerits;
  }

  public async shouldBePenalisedForAvailability(user: UserProfile, dateOfRequest: Date): Promise<boolean> {
    if (user.demerits) {
      const toRange = dateOfRequest;
      const fromRange = new Date(toRange.getTime()
                - (BookingsConfig.daysThatDemeritsAreConsideredFor * 24 * 60 * 60 * 1000));
      return this.getDemerits(user, fromRange, toRange).length > 1;
    }
    return false;
  }

  public async isPenalisedWithDemerits(user: UserProfile, sessionStartTime: Date, dateOfRequest: Date)
    : Promise<boolean> {
    const daysToPad = BookingsConfig.maxNumberOfDaysThatDemerittedUserCanGetAvailabilityFor
            * 24 * 60 * 60 * 1000;
    const definedNumberOfDaysFromNowTimeStamp = new Date(dateOfRequest.getTime() + daysToPad).getTime();
    if ((sessionStartTime.getTime() >= definedNumberOfDaysFromNowTimeStamp)) {
      return this.shouldBePenalisedForAvailability(user, sessionStartTime);
    }
    return false;
  }
}
